import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import "./WordTable.css";

// MUI imports for multi-select
import {
  OutlinedInput,
  InputLabel,
  MenuItem,
  FormControl,
  ListItemText,
  Select,
  Checkbox,
} from "@mui/material";

const ITEM_HEIGHT = 40;
const ITEM_PADDING_TOP = 4;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 140,
    },
  },
};

const WordTable = () => {
  const [mockData, setmockData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [filters, setFilters] = useState({
    Word: "",
    Pron: "",
    Pos: [],
    Etym: "",
    Quizlevel: "",
    DateFrom: "",
    DateTo: "",
  });

  const posOptions = [
    "noun",
    "verb",
    "adjective",
    "adverb",
    "preposition",
    "conjunction",
    "interjection",
  ];

  useEffect(() => {
    axios
      .get("https://s3napi-dev.s3nsoftware.com/api/WordOfTheDay/List")
      .then((res) => setmockData(res.data))
      .catch((err) => console.error("Failed to fetch:", err));
  }, []);
  useEffect(() => {
    setCurrentPage(1); // Reset to page 1 on any filter change
  }, [filters]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      Word: "",
      Pron: "",
      Pos: [],
      Etym: "",
      Quizlevel: "",
      DateFrom: "",
      DateTo: "",
    });
  };

  const filteredData = useMemo(() => {
    return mockData.filter((item) => {
      const wordMatch = (item.Word || "")
        .toLowerCase()
        .includes(filters.Word.toLowerCase());
      const pronMatch = (item.Pron || "")
        .toLowerCase()
        .includes(filters.Pron.toLowerCase());
      const posMatch =
        filters.Pos.length === 0 ||
        filters.Pos.includes((item.Pos || "").toLowerCase());
      const etymMatch = (item.Etym || "")
        .toLowerCase()
        .includes(filters.Etym.toLowerCase());
      const quizMatch =
        filters.Quizlevel === "" ||
        item.Quizlevel === Number(filters.Quizlevel);
      const date = new Date(item.Date);
      const from = filters.DateFrom ? new Date(filters.DateFrom) : null;
      const to = filters.DateTo ? new Date(filters.DateTo) : null;
      const dateMatch = (!from || date >= from) && (!to || date <= to);

      return (
        wordMatch &&
        pronMatch &&
        posMatch &&
        etymMatch &&
        quizMatch &&
        dateMatch
      );
    });
  }, [filters, mockData]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredData, currentPage]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const queryString = useMemo(() => {
    const queryParts = [];
    if (filters.Word) queryParts.push(`Word CONTAINS "${filters.Word}"`);
    if (filters.Pron) queryParts.push(`Pron CONTAINS "${filters.Pron}"`);
    if (filters.Pos.length > 0)
      queryParts.push(`Pos IN (${filters.Pos.join(", ")})`);
    if (filters.Etym) queryParts.push(`Etym CONTAINS "${filters.Etym}"`);
    if (filters.Quizlevel) queryParts.push(`Quizlevel = ${filters.Quizlevel}`);
    if (filters.DateFrom) queryParts.push(`Date >= ${filters.DateFrom}`);
    if (filters.DateTo) queryParts.push(`Date <= ${filters.DateTo}`);
    return queryParts.length > 0
      ? queryParts.join(" AND ")
      : "No filters applied";
  }, [filters]);

  return (
    <div style={{ padding: "20px" }}>
      {/* <h2>Word Table with Filters</h2> */}

      <div className="query-box">
        <strong>Query:</strong> <code>{queryString}</code>
      </div>

      <div className="filters-row">
        <input
          name="Word"
          placeholder="Word"
          value={filters.Word}
          onChange={handleChange}
        />
        <input
          name="Pron"
          placeholder="Pronunciation"
          value={filters.Pron}
          onChange={handleChange}
        />

        <FormControl sx={{ minWidth: 200, mt: 1 }}>
          <InputLabel
            id="pos-label"
            sx={{ fontFamily: "Segoe UI", fontSize: 14 }}
          >
            Part of Speech
          </InputLabel>
          <Select
            labelId="pos-label"
            id="pos-select"
            multiple
            name="Pos"
            value={filters.Pos}
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                Pos:
                  typeof e.target.value === "string"
                    ? e.target.value.split(",")
                    : e.target.value,
              }))
            }
            input={
              <OutlinedInput
                label="Part of Speech"
                sx={{
                  height: 40,
                  display: "flex",
                  alignItems: "center",
                  fontFamily: "Segoe UI",
                  fontSize: 14,
                }}
              />
            }
            renderValue={(selected) => selected.join(", ")}
            MenuProps={MenuProps}
            sx={{
              height: 40,
              display: "flex",
              alignItems: "center",
              fontFamily: "Segoe UI",
              fontSize: 14,
            }}
          >
            {posOptions.map((pos) => (
              <MenuItem key={pos} value={pos}>
                <Checkbox checked={filters.Pos.indexOf(pos) > -1} />
                <ListItemText
                  primary={pos}
                  sx={{ fontFamily: "Segoe UI", fontSize: 14 }}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <input
          name="Etym"
          placeholder="Etymology"
          value={filters.Etym}
          onChange={handleChange}
        />
        <select
          name="Quizlevel"
          value={filters.Quizlevel}
          onChange={handleChange}
        >
          <option value="">All Levels</option>
          <option value="1">1</option>
          <option value="2">2</option>
          <option value="3">3</option>
        </select>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginRight: "10px",
          }}
        >
          <span style={{ fontSize: "14px" }}>From:</span>
          <input
            name="DateFrom"
            type="date"
            value={filters.DateFrom}
            onChange={handleChange}
            style={{ height: "35px", padding: "4px" }}
          />
          <span style={{ fontSize: "14px" }}>To:</span>
          <input
            name="DateTo"
            type="date"
            value={filters.DateTo}
            onChange={handleChange}
            style={{ height: "35px", padding: "4px" }}
          />
        </div>

        <button onClick={handleClearFilters} className="clear-btn">
          Clear
        </button>
      </div>

      <table className="word-table">
        <thead>
          <tr>
            <th>Word</th>
            <th>Pronunciation</th>
            <th>POS</th>
            <th>Definition</th>
            <th>Etymology</th>
            <th>Quiz Level</th>
            <th>Date</th>
          </tr>
        </thead>
        <tbody>
          {filteredData.length ? (
            paginatedData.map((row, i) => (
              <tr key={i}>
                <td>{row.Word}</td>
                <td>{row.Pron}</td>
                <td>{row.Pos}</td>
                <td>{row.Defn}</td>
                <td>{row.Etym}</td>
                <td>{row.Quizlevel}</td>
                <td>{new Date(row.Date).toLocaleDateString()}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="7" style={{ textAlign: "center" }}>
                No matching data
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <div className="pagination">
        <button
          onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
          disabled={currentPage === 1}
        >
          &laquo; Prev
        </button>
        {[...Array(totalPages)].map((_, index) => (
          <button
            key={index}
            className={currentPage === index + 1 ? "active" : ""}
            onClick={() => setCurrentPage(index + 1)}
          >
            {index + 1}
          </button>
        ))}
        <button
          onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
          disabled={currentPage === totalPages}
        >
          Next &raquo;
        </button>
      </div>
    </div>
  );
};

export default WordTable;
